import { type FC, type ReactNode, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import EventContext, { type EventPack } from './EventContext';
import { type ActionPayloadMap } from './types.interface';

type Callback<T extends keyof ActionPayloadMap> = (payload: ActionPayloadMap[T]) => Promise<void> | void;

type State<T extends keyof ActionPayloadMap> = Record<T, Array<Callback<T>>>;

type SubscribeAction<T extends keyof ActionPayloadMap> = {
  type: 'subscribe';
  event: T;
  callback: Callback<T>;
};
type UnsubscribeAction<T extends keyof ActionPayloadMap> = {
  type: 'unsubscribe';
  event: T;
  callback: Callback<T>;
};

export type Action<T extends keyof ActionPayloadMap> = SubscribeAction<T> | UnsubscribeAction<T>;

const EventEmitterProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const [subscribers, dispatch] = useReducer(
    <T extends keyof ActionPayloadMap>(state: State<T>, action: Action<T>) => {
      const { type, event } = action;
      switch (type) {
        case 'subscribe': {
          const { callback } = action;
          if (event in state) {
            if (state[event].includes(callback)) {
              return state;
            }
            return { ...state, [event]: [...state[event], callback] };
          }
          return { ...state, [event]: [callback] };
        }

        case 'unsubscribe': {
          const { callback } = action;
          if (event in state && state[event].includes(callback)) {
            return { ...state, [event]: state[event].filter((cb) => cb !== callback) };
          }
          return state;
        }

        default:
          throw new Error('Unhandled action type in EventEmitterProvider');
      }
    },
    {} as State<keyof ActionPayloadMap>,
  );

  const subscribersRef = useRef(subscribers);
  useEffect(() => {
    subscribersRef.current = subscribers;
  }, [subscribers]);

  /**
   * Dispatches an event with a payload.
   * @param event - The event to dispatch.
   * @param payload - The payload associated with the event.
   */
  const dispatchEvent = useCallback(
    async <T extends keyof ActionPayloadMap>(event: T, payload: ActionPayloadMap[T]) => {
      if (event in (subscribersRef?.current ?? {})) {
        await Promise.all(
          subscribersRef?.current[event].map(async (cb) => {
            await cb(payload);
          }),
        );
      }
    },
    [],
  );

  /**
   * Subscribes a callback function to a specific event.
   * @param event - The event to subscribe to.
   * @param cb - The callback function to be invoked when the event occurs.
   */
  const subscribe = useCallback(<T extends keyof ActionPayloadMap>(event: T, callback: Callback<T>) => {
    dispatch({ type: 'subscribe', event, callback: callback as Callback<keyof ActionPayloadMap> });
  }, []);

  /**
   * Unsubscribes a callback function from a specific event.
   * @param event - The event to unsubscribe from.
   * @param cb - The callback function to be removed.
   */
  const unsubscribe = useCallback(
    <T extends keyof ActionPayloadMap>(event: T, callback: Callback<T>) => {
      dispatch({ type: 'unsubscribe', event, callback: callback as Callback<keyof ActionPayloadMap> });
    },
    [dispatch],
  );

  const eventPack = useMemo(
    (): EventPack => [subscribe, unsubscribe, dispatchEvent],
    [subscribe, unsubscribe, dispatchEvent],
  );

  return <EventContext.Provider value={eventPack}>{children}</EventContext.Provider>;
};

export default EventEmitterProvider;
